// src/hooks/useServiceCategories.ts - Hooks pour gérer les catégories de services
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  query,
  where,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { COLLECTIONS } from '../config/collections';
import { ServiceCategory } from '../types/entities';
import { useAuth } from '../contexts/AuthContext';
import { handleFirestoreError } from '../utils/firestoreErrorHandler';
import { usePermissions } from './usePermissions';
import { message } from 'antd';
import { useAppTranslation } from './useAppTranslation';
import { getErrorMessage } from '../utils/i18n/getErrorMessage';
import { AppError } from '../utils/i18n/AppError';

// ===== HOOKS POUR LES CATÉGORIES DE SERVICES =====

// Hook pour récupérer toutes les catégories de services
export const useServiceCategories = () => {
  const { currentBusiness } = useAuth();
  const permissions = usePermissions();
  const businessId = currentBusiness?.id;

  return useQuery({
    queryKey: ['serviceCategories', businessId],
    queryFn: async (): Promise<ServiceCategory[]> => {
      if (!businessId) {
        console.warn('Aucun businessId disponible');
        return [];
      }

      try {
        const q = query(
          collection(db, COLLECTIONS.serviceCategories),
          where('businessId', '==', businessId),
          where('isActive', '==', true)
        );
        
        const snapshot = await getDocs(q).catch(error => {
          handleFirestoreError(error, 'useServiceCategories - Main query');
          throw error;
        });
        
        const categories = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            businessId: data.businessId,
            name: data.name,
            description: data.description || '',
            color: data.color,
            isActive: data.isActive,
            createdAt: data.createdAt?.toDate() || new Date(),
            updatedAt: data.updatedAt?.toDate() || new Date(),
          };
        }) as ServiceCategory[];
        
        console.log('📂 Catégories de services récupérées:', categories.length);
        return categories;
      } catch (error) {
        console.error('❌ Erreur lors de la récupération des catégories:', error);
        throw error;
      }
    },
    enabled: !!businessId && (
      (permissions?.canViewServices ?? false) ||
      (permissions?.canCreatePrestations ?? false) ||
      (permissions?.canViewPrestations ?? false) ||
      (permissions?.canViewCashReports ?? false) ||
      (permissions?.canViewDashboard ?? false) ||
      (permissions?.canViewAccounting ?? false)
    ),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

// Hook pour créer une nouvelle catégorie
export const useCreateServiceCategory = () => {
  const queryClient = useQueryClient();
  const { currentBusiness } = useAuth();
  const { t } = useAppTranslation(['settings', 'errors']);
  const businessId = currentBusiness?.id;
  
  return useMutation({
    mutationFn: async (categoryData: Omit<ServiceCategory, 'id' | 'businessId' | 'createdAt' | 'updatedAt'>) => {
      if (!businessId) {
        throw new AppError('businessUndefined');
      }
      
      const now = new Date();
      const newCategory = {
        ...categoryData,
        businessId,
        isActive: true,
        createdAt: Timestamp.fromDate(now),
        updatedAt: Timestamp.fromDate(now),
      };
      
      console.log('➕ Création de la catégorie:', newCategory);
      const docRef = await addDoc(collection(db, COLLECTIONS.serviceCategories), newCategory);
      return docRef.id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['serviceCategories', businessId] });
      message.success(t('serviceCategories.createSuccess'));
    },
    onError: (error: unknown) => {
      console.error('❌ Erreur lors de la création de la catégorie:', error);
      message.error(getErrorMessage(error, t) || t('serviceCategories.createError'));
    },
  });
};

// Hook pour modifier une catégorie
export const useUpdateServiceCategory = () => {
  const queryClient = useQueryClient();
  const { currentBusiness } = useAuth();
  const { t } = useAppTranslation(['settings', 'errors']);
  const businessId = currentBusiness?.id;
  
  return useMutation({
    mutationFn: async ({ id, ...updateData }: Partial<ServiceCategory> & { id: string }) => {
      if (!businessId) {
        throw new AppError('businessUndefined');
      }
      
      const now = new Date();
      const categoryUpdate = {
        ...updateData,
        updatedAt: Timestamp.fromDate(now),
      };
      
      // Supprimer les champs qui ne doivent pas être mis à jour
      if ('id' in categoryUpdate) delete categoryUpdate.id;
      if ('businessId' in categoryUpdate) delete categoryUpdate.businessId;
      if ('createdAt' in categoryUpdate) delete categoryUpdate.createdAt;
      
      console.log('✏️ Mise à jour de la catégorie:', id, categoryUpdate);
      await updateDoc(doc(db, COLLECTIONS.serviceCategories, id), categoryUpdate);
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['serviceCategories', businessId] });
      message.success(t('serviceCategories.updateSuccess'));
    },
    onError: (error: unknown) => {
      console.error('❌ Erreur lors de la modification de la catégorie:', error);
      message.error(getErrorMessage(error, t) || t('serviceCategories.updateError'));
    },
  });
};

// Hook pour supprimer une catégorie (soft delete)
export const useDeleteServiceCategory = () => {
  const queryClient = useQueryClient();
  const { currentBusiness } = useAuth();
  const { t } = useAppTranslation(['settings', 'errors']);
  const businessId = currentBusiness?.id;
  
  return useMutation({
    mutationFn: async (categoryId: string) => {
      if (!businessId) {
        throw new AppError('businessUndefined');
      }
      
      // Vérifier s'il y a des services utilisant cette catégorie
      const servicesQuery = query(
        collection(db, COLLECTIONS.services),
        where('businessId', '==', businessId),
        where('categoryId', '==', categoryId)
      );
      
      const servicesSnap = await getDocs(servicesQuery).catch(error => {
        handleFirestoreError(error, 'useServiceCategories - Services query');
        throw error;
      });
      const servicesCount = servicesSnap.docs.length;
      
      if (servicesCount > 0) {
        throw new AppError('serviceCategories.deleteInUse', { count: servicesCount });
      }
      
      // Soft delete : marquer comme inactive
      const now = new Date();
      await updateDoc(doc(db, COLLECTIONS.serviceCategories, categoryId), {
        isActive: false,
        updatedAt: Timestamp.fromDate(now),
      });
      
      console.log('🗑️ Suppression de la catégorie:', categoryId);
      return categoryId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['serviceCategories', businessId] });
      queryClient.invalidateQueries({ queryKey: ['serviceCategoryStats', businessId] });
      message.success(t('serviceCategories.deleteSuccess'));
    },
    onError: (error: unknown) => {
      console.error('❌ Erreur lors de la suppression de la catégorie:', error);
      message.error(getErrorMessage(error, t) || t('serviceCategories.deleteError'));
    },
  });
};


// Hook utilitaire pour obtenir une catégorie par ID
export const useServiceCategory = (categoryId: string | undefined) => {
  const { data: categories, isLoading, error } = useServiceCategories();
  
  const category = categories?.find(cat => cat.id === categoryId);
  
  return {
    category,
    isLoading,
    error,
  };
};

// Hook pour obtenir les statistiques des catégories (nombre de services par catégorie)
export const useServiceCategoryStats = () => {
  const { currentBusiness } = useAuth();
  const permissions = usePermissions();
  const businessId = currentBusiness?.id;
  
  return useQuery({
    queryKey: ['serviceCategoryStats', businessId],
    queryFn: async () => {
      if (!businessId) {
        return {};
      }
      
      try {
        // Récupérer tous les services pour compter par catégorie
        const servicesQuery = query(
          collection(db, COLLECTIONS.services),
          where('businessId', '==', businessId)
        );
        
        const servicesSnap = await getDocs(servicesQuery).catch(error => {
        handleFirestoreError(error, 'useServiceCategories - Services query');
        throw error;
      });
        
        const stats: Record<string, number> = {};
        servicesSnap.docs.forEach(doc => {
          const data = doc.data();
          const categoryId = data.categoryId || data.category; // Support migration
          if (categoryId) {
            stats[categoryId] = (stats[categoryId] || 0) + 1;
          }
        });
        
        console.log('📊 Statistiques des catégories:', stats);
        return stats;
      } catch (error) {
        console.error('❌ Erreur lors du calcul des statistiques:', error);
        return {};
      }
    },
    enabled: !!businessId && (permissions?.canViewServices ?? false),
    staleTime: 2 * 60 * 1000, // 2 minutes
  });
};
